import React from 'react';
import { observer } from 'mobx-react';
import { useContext } from 'react';
import { AppContext } from '/@/app/AppContext';
import useTheme from '@mui/material/styles/useTheme';
import { styled } from '@mui/material/styles';
import { Box, Typography, TextField, Tooltip} from '@mui/material';
import { numberOrNull } from '/@/app/utils';
//
import InputAdornment from '@mui/material/InputAdornment';
import HelpIcon from '@mui/icons-material/Help';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CalculateOutlinedIcon from '@mui/icons-material/CalculateOutlined';
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
//
import { AudioPlayer } from '/@/app/components/AudioPlayer';
import { MeasurementModalStateContext } from '/@/app/views/LabTestView/MeasurementModalState';
import { UnitsSelector } from '/@/app/views/LabTestView/UnitsSelector';


export const MeasurementItem = styled(Box)(({ theme }) => ({
    ...theme.typography.body2,
    margin: theme.spacing(1),
    textAlign: 'center',
    color: theme.palette.text.secondary,

    // content to bottom,
    // so it will align with neighboring items on wide screens
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flexFlow: 'column nowrap',
}));


export const Measurement = observer(({ mState }) => {
    const theme = useTheme();
    const modalState = useContext(MeasurementModalStateContext);

    let m = mState.measurement;  // XXX can be null

    const colorFromSaveState = (saveState) => {
        let color = {
            "unsaved": "rgba(100,100,255,0.1)",
            "verified": "rgba(100,255,100,0.1)",
            "error": "rgba(255,0,0,0.1)",
            "saved": "white",
        }[saveState];
        if (color === undefined) {
            color = "white";
        }
        return color;
    }

    const onChange = (event) => {
        // clean value
        let value = event.target.value;
        value = value.replace(/[^0-9., -]/g, '');  // clear invalid symbols
        value = value.replace(/,/g, '.');  // comma to dot
        value = value.replace(/(\..*)\./g, '$1');  // only one dot allowed
        mState.setTypedText(value);
        mState.setInputValue(value);
    }

    const dynamics = useContext(AppContext).bootstrapData?.panel_dynamics?.items?.[mState?.xlsxId || 0];
    const commentsActive = (
        m?.comments
        || mState.article_link
        || dynamics
    );
    const openCommentDialog = () => {
        if (!commentsActive) return;
        modalState.show(mState);
    }

    // Mark optimal / non optimal
    let nonOptimal = mState.nonOptimal;
    let adornment = (
        mState.value_is_computed ?
            <CalculateOutlinedIcon />
            : <InfoOutlinedIcon sx={{ color: "transparent" }} />  // placeholder
    )
    if (numberOrNull(mState.value?.value) !== null) {  // use non-alternative here
        if (m && (m.warning_bottom_edge !== null || m.warning_top_edge !== null)) {
            if (nonOptimal) {
                adornment = (
                    <Tooltip title="За границами оптимального">
                        <ErrorIcon
                            color="error"
                            onClick={openCommentDialog}
                            sx={{ cursor: commentsActive ? "pointer" : "default" }}
                        />
                    </Tooltip>
                );
            } else {
                adornment = (
                    <Tooltip title="Оптимально">
                        <CheckCircleIcon
                            color="success"
                            onClick={openCommentDialog}
                            sx={{ cursor: commentsActive ? "pointer" : "default" }}
                        />
                    </Tooltip>
                );
            }
        } else {
            adornment = <CheckCircleIcon sx={{ color: "#dfdfdf" }} />
        }
    }

    return (
        <>
            <Typography
                variant="body1"
                component="div"
                sx={{
                    lineHeight: '1.2',  // it's actually a header - make compact
                    marginBottom: '5px',
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'stretch',
                    alignItems: 'center',
                    width: '100%',
                    cursor: commentsActive ? "pointer" : "default",
                }}

                gutterBottom
            >
                <Box
                    sx={{ flexGrow: "1" }}
                    onClick={openCommentDialog}
                >{mState.caption}</Box>

                <AudioPlayer audioSrc={mState.sound_link} sx={{
                    justifySelf: "left",
                }} size="20px" hideCaption={true} />
                {commentsActive &&
                    <HelpIcon
                        sx={{
                            marginLeft: '5px',
                            color: theme.palette.primary.main,
                            //
                            justifySelf: "right",
                            cursor: "pointer",
                            filter: "brightness(180%) saturate(70%)",
                        }}
                        onClick={openCommentDialog}
                    />
                }
            </Typography>
            <TextField
                label={mState.value_is_computed ? "расчётный параметр" : ""}
                disabled={mState.value_is_computed}
                variant="outlined"
                value={
                    mState.typedText ? mState.typedText :
                        (mState.value?.valueAlternativeUnits !== null
                            ? Math.round(mState.value.valueAlternativeUnits * 1000) / 1000
                            : ""
                        )
                }
                onKeyDown={onChange}
                onChange={onChange}
                error={mState.saveState === "error"}
                sx={{
                    width: "100%",
                    input: {
                        textAlign: 'center',
                        backgroundColor: colorFromSaveState(mState.saveState),
                        borderRadius: '15px',  // magic number to match the shape of the outlined input
                        pr: '20px',
                        pl: '60px',
                    },
                    '& .MuiFormHelperText-root': { textAlign: 'right', paddingRight: '40px' }, // helper text
                    'input.Mui-disabled': { backgroundColor: '#f5f7fb' }, // ?? label hides when this is set
                }}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">{adornment}</InputAdornment>
                    ),
                }}
            />
            <Box sx={{
                color: theme.palette.text.secondary,
                display: "flex",
                flexFlow: "row nowrap",
                justifyContent: "space-between",
                textAlign: "left",
                alignSelf: 'stretch',
                lineHeight: '1.2',
                marginLeft: 1,
                marginRight: 0
            }}>
                <Box sx={{
                    flexShrink: "1",
                }}>
                    {mState.measurement?.target_range &&
                        <>
                            Оптимально:{" "}
                            {mState.measurement.target_range.replace(/(\d+),(\d+)/g, "$1.$2")}{" "}
                            {/*
                             If alternative used, show default units here!
                            */}
                            {(mState.value?.alternativeUnits) &&
                                <>{mState.defaultUnits}</>
                            }
                        </>
                    }
                </Box>
                <UnitsSelector mState={mState} sx={{
                    ml: 2,
                    flexShrink: "0",
                }} />
            </Box>
        </>
    )
});
